import { useState, useRef, useEffect } from 'react';

// Global audio instance registry to track all playing audio elements
const activeAudioInstances = new Set<HTMLAudioElement>();

// Global cleanup function to stop all active audio instances
export const stopAllAudio = () => {
  activeAudioInstances.forEach(audio => {
    try {
      audio.pause();
      audio.src = '';
    } catch (e) {
      console.error('Error stopping audio:', e);
    }
  });
  activeAudioInstances.clear();
};

interface UseAudioPlayerProps {
  maxDuration?: number;
}

export const useAudioPlayer = ({ maxDuration = 30 }: UseAudioPlayerProps = {}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = ''; // Clear the source to release resources
      audioRef.current.removeEventListener('ended', handleEnded);
      audioRef.current.removeEventListener('error', handleError);
      // Remove from global registry
      activeAudioInstances.delete(audioRef.current);
      audioRef.current = null;
    }
    setIsPlaying(false);
    setLoading(false);
    setError(null);
  };

  useEffect(() => {
    return cleanup;
  }, []);

  const handleEnded = () => {
    cleanup();
  };

  const handleError = (e: Event) => {
    const target = e.target as HTMLAudioElement;
    let errorMessage = 'Unable to play audio. Please try again.';

    if (target.error) {
      switch (target.error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = 'Audio playback was aborted.';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMessage = 'A network error occurred while loading the audio.';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMessage = 'Audio decoding failed. The format might not be supported.';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'This audio format is not supported by your browser.';
          break;
      }
    }

    console.error('Audio Playback Error:', {
      code: target.error?.code,
      message: errorMessage,
      src: target.src
    });

    setError(errorMessage);
    cleanup();
  };

  const validateAudioFormat = (url: string): boolean => {
    if (!url) return false;

    if (url.startsWith('data:audio/')) {
      const base64Data = url.split(',')[1];
      return !!base64Data && base64Data.length > 0;
    }

    const validExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];
    return validExtensions.some(ext => url.toLowerCase().endsWith(ext));
  };

  const play = async (url: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      if (!validateAudioFormat(url)) {
        throw new Error('Invalid or empty audio data');
      }
      
      // Stop all currently playing audio to prevent echo/dual channel issues
      stopAllAudio();
      cleanup();

      const audio = new Audio();
      audio.preload = 'auto';

      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      const loadPromise = new Promise((resolve, reject) => {
        audio.addEventListener('canplaythrough', resolve, { once: true });
        audio.addEventListener('error', reject, { once: true });
      });

      audio.src = url;
      audio.load();

      await loadPromise;
      
      audioRef.current = audio;
      // Add to global registry
      activeAudioInstances.add(audio);
      await audio.play();
      setIsPlaying(true);

      timeoutRef.current = setTimeout(() => {
        cleanup();
      }, maxDuration * 1000);
    } catch (err: any) {
      console.error('Audio Play Error:', {
        message: err.message,
        url,
      });
      setError(err.message || 'Failed to play audio');
      cleanup();
    } finally {
      setLoading(false);
    }
  };

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      cleanup();
    }
  };

  return {
    isPlaying,
    error,
    loading,
    play,
    stop,
  };
};

export default useAudioPlayer;